/**
 * In-browser API mock for the `contract-out-licensing` prototype.
 *
 * Registers its routes with the shared dev mode (src/lib/dev-mode), so with the
 * auth bypass on, contracts in, contracts out, licensees and the revenue upload
 * all work with no Django backend.
 *
 * Scope: contracts (both directions), the catalogue their track pickers reach
 * for, licensees, and the vendor list the revenue upload offers. Every other
 * screen keeps dev-mode's empty default — it is not part of this prototype.
 *
 * Edits are kept in localStorage, so a reviewer's contract out survives a
 * reload; "Reset prototype" in the dev bar puts the seed back.
 */
import { API_BASE_URL } from "@/lib/api/core";
import { jsonResponse, paginated, registerDevMockRoutes } from "@/lib/dev-mode";
import {
  LABEL_ID,
  LABEL_NAME,
  PAYEES,
  RECORDINGS,
  RELEASES,
  SHOW_LICENSEE_STATEMENTS,
  VENDORS,
  seedContracts,
  seedContractsOut,
  seedLicensees,
  seedRevenueStatements,
  seedSimpleSales,
  type StoredContract,
  type StoredContractOut,
  type StoredSimpleSalesEntry,
} from "./fixtures";
import type { Licensee } from "@/types";
import { convertToLabelCurrency } from "./exchangeRates";

const STORE_KEY = "col-store-v2";

type Store = {
  contracts: StoredContract[];
  contractsOut: StoredContractOut[];
  licensees: Licensee[];
  revenueStatements: ReturnType<typeof seedRevenueStatements>;
  simpleSales: StoredSimpleSalesEntry[];
};

function seed(): Store {
  return {
    contracts: seedContracts(),
    contractsOut: seedContractsOut(),
    licensees: seedLicensees(),
    revenueStatements: seedRevenueStatements(),
    simpleSales: seedSimpleSales(),
  };
}

function load(): Store {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const stored = JSON.parse(raw) as Store;
      // A store saved before revenue was part of the prototype gets its seed.
      stored.revenueStatements ??= seedRevenueStatements();
      stored.simpleSales ??= seedSimpleSales();
      return stored;
    }
  } catch {
    /* corrupt or unavailable storage: fall back to a fresh seed */
  }
  const fresh = seed();
  save(fresh);
  return fresh;
}

function save(store: Store): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    /* private mode / quota: the prototype still works for this page view */
  }
}

/** Put the prototype back to its seeded state. Called by the dev bar. */
export function resetPrototype(): void {
  localStorage.removeItem(STORE_KEY);
}

function newId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

const nowIso = () => new Date().toISOString();

// --- Catalogue -------------------------------------------------------------

function releaseSummary(releaseId: string) {
  const release = RELEASES.find((r) => r.id === releaseId);
  if (!release) return null;
  return {
    id: release.id,
    title: release.title,
    type: release.type,
    release_date: release.release_date,
    artwork: null,
    tracks_count: RECORDINGS.filter((rec) => rec.release === release.id).length,
  };
}

function recordingPayload(id: string) {
  const recording = RECORDINGS.find((rec) => rec.id === id);
  if (!recording) return null;
  const release = releaseSummary(recording.release);
  return {
    id: recording.id,
    title: recording.title,
    mix_name: recording.mix_name,
    track_number: recording.track_number,
    isrc: recording.isrc,
    contributors: [
      {
        id: `contrib-${recording.id}`,
        display_name: recording.artist,
        role: "primary_artist",
        country: "",
      },
      ...(recording.featuring
        ? [
            {
              id: `contrib-${recording.id}-featuring`,
              display_name: recording.featuring,
              role: "featured_artist",
              country: "",
            },
          ]
        : []),
    ],
    releases: release ? [release] : [],
    can_delete: true,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
}

/** The tracks of a contract (in or out), in the app's AssociatedTrack shape. */
function tracksPayload(trackIds: string[]) {
  return trackIds.map((id) => recordingPayload(id)).filter(Boolean);
}

/** Every release the given tracks belong to, each listed once. */
function releasesPayload(trackIds: string[]) {
  const releaseIds = new Set(
    trackIds
      .map((id) => RECORDINGS.find((rec) => rec.id === id)?.release)
      .filter((id): id is string => !!id),
  );
  return [...releaseIds].map((id) => releaseSummary(id)).filter(Boolean);
}

// --- Contracts in ----------------------------------------------------------

function contractPayload(contract: StoredContract, store: Store) {
  return {
    ...contract,
    label: LABEL_ID,
    label_name: LABEL_NAME,
    party: "",
    starting_balance: null,
    created_at: nowIso(),
    updated_at: nowIso(),
    first_sound_recording_title:
      RECORDINGS.find((rec) => rec.id === contract.track_ids[0])?.title ?? null,
    tracks_count: contract.track_ids.length,
    all_tracks_have_isrc: true,
    can_delete: true,
    can_activate: contract.track_ids.length > 0 && contract.payees.length > 0,
    payees: contract.payees.map((assignment) => {
      const payee = [...PAYEES, ...store.licensees].find((p) => p.id === assignment.payee);
      return {
        matched: true,
        share_percentage: assignment.share_percentage,
        opening_balance: "0.00",
        payee: {
          id: assignment.payee,
          name: payee?.name ?? "Unknown payee",
          legal_name: payee?.legal_name ?? null,
          city: payee?.city ?? null,
          country: payee?.country ?? null,
        },
      };
    }),
  };
}

function contractDetailPayload(contract: StoredContract, store: Store) {
  return {
    ...contractPayload(contract, store),
    related_recordings: tracksPayload(contract.track_ids),
    related_releases: releasesPayload(contract.track_ids),
    has_revenue: false,
    has_expenses: false,
  };
}

// --- Contracts out ---------------------------------------------------------

/** The contract in a licensed track earns under — who the licensing revenue reaches. */
function fromContract(trackId: string, store: Store) {
  const contract = store.contracts.find((c) => c.track_ids.includes(trackId));
  return contract ? { id: contract.id, name: contract.name } : null;
}

function contractOutPayload(contractOut: StoredContractOut, store: Store) {
  const licensee = store.licensees.find((l) => l.id === contractOut.licensee);
  return {
    ...contractOut,
    label: LABEL_ID,
    created_at: nowIso(),
    updated_at: nowIso(),
    tracks_count: contractOut.track_ids.length,
    first_track_title: RECORDINGS.find((rec) => rec.id === contractOut.track_ids[0])?.title ?? null,
    licensee_details: licensee ? { id: licensee.id, name: licensee.name } : null,
    can_activate: contractOut.track_ids.length > 0 && !!licensee,
  };
}

function contractOutDetailPayload(contractOut: StoredContractOut, store: Store) {
  return {
    ...contractOutPayload(contractOut, store),
    licensed_tracks: contractOut.track_ids
      .map((id) => {
        const track = recordingPayload(id);
        return track ? { ...track, from_contract: fromContract(id, store) } : null;
      })
      .filter(Boolean),
    related_releases: releasesPayload(contractOut.track_ids),
  };
}

// --- Simple sales ------------------------------------------------------------

function simpleSalesPayload(entry: StoredSimpleSalesEntry, store: Store) {
  const contractOut = entry.contract_out
    ? store.contractsOut.find((c) => c.id === entry.contract_out)
    : undefined;
  const release = entry.release_id ? RELEASES.find((r) => r.id === entry.release_id) : null;
  const lines = entry.lines
    .map((line) => {
      const recording = RECORDINGS.find((rec) => rec.id === line.track_id);
      if (!recording) return null;
      return {
        track: {
          id: recording.id,
          title: recording.title,
          mix_name: recording.mix_name,
          contributors: recordingPayload(recording.id)?.contributors ?? [],
          releases: recordingPayload(recording.id)?.releases ?? [],
        },
        artist: [recording.artist, recording.featuring].filter(Boolean).join(", "),
        amount: line.amount,
        units: line.units ?? null,
      };
    })
    .filter((line): line is NonNullable<typeof line> => !!line);
  // A release entry carries one amount; a tracks entry adds its tracks up.
  const total = release
    ? Number(entry.amount) || 0
    : entry.lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  // The same conversion the form previews, so the stored figure is the one shown.
  const conversion =
    entry.currency === "USD"
      ? null
      : convertToLabelCurrency(total, entry.currency, entry.sales_date);
  return {
    id: entry.id,
    label: LABEL_ID,
    revenue_type: entry.revenue_type,
    contract_out: contractOut
      ? {
          id: contractOut.id,
          name: contractOut.name,
          licensee_name: licenseeNameOf(contractOut, store) || null,
        }
      : null,
    sales_date: entry.sales_date,
    currency: entry.currency,
    release: release ? { id: release.id, title: release.title } : null,
    units: release ? (entry.units ?? null) : null,
    lines,
    total: total.toFixed(2),
    converted_total: conversion ? conversion.converted.toFixed(2) : null,
    exchange_rate: conversion ? String(conversion.rate) : null,
    country: entry.country,
    reference: entry.reference,
    notes: entry.notes,
    created_at: entry.created_at,
    updated_at: entry.created_at,
  };
}

function storedSimpleSalesFrom(
  body: Record<string, any>,
  previous?: StoredSimpleSalesEntry,
): StoredSimpleSalesEntry {
  const revenueType = body.revenue_type ?? previous?.revenue_type ?? "digital";
  const physical = revenueType === "physical";
  return {
    id: previous?.id ?? newId("sale"),
    revenue_type: revenueType,
    contract_out:
      revenueType === "licensing" ? (body.contract_out ?? previous?.contract_out ?? null) : null,
    sales_date: body.sales_date ?? previous?.sales_date ?? "",
    currency: body.currency ?? previous?.currency ?? "USD",
    release_id: physical ? (body.release_id ?? previous?.release_id ?? null) : null,
    amount: physical ? (body.amount ?? previous?.amount ?? null) : null,
    units: physical ? (body.units ?? previous?.units ?? null) : null,
    lines: physical
      ? []
      : (body.lines ?? previous?.lines ?? []).map((line: any) => ({
          track_id: line.track_id,
          amount: line.amount,
          units: revenueType === "digital" ? (line.units ?? null) : null,
        })),
    country: body.country ?? previous?.country ?? null,
    reference: body.reference ?? previous?.reference ?? null,
    notes: body.notes ?? previous?.notes ?? null,
    created_at: previous?.created_at ?? nowIso(),
  };
}

// --- Routing ---------------------------------------------------------------

function basePath(): string {
  return new URL(API_BASE_URL, window.location.origin).pathname.replace(/\/$/, "");
}

function parseBody(body: BodyInit | null | undefined): Record<string, any> {
  if (!body) return {};
  if (typeof body === "string") {
    try {
      return JSON.parse(body);
    } catch {
      return {};
    }
  }
  if (body instanceof FormData) {
    return Object.fromEntries(body.entries());
  }
  return {};
}

/** The licensee's name — the mock's own copy of licenseeName(). */
function licenseeNameOf(contractOut: StoredContractOut, store: Store): string {
  return (
    store.licensees.find((l) => l.id === contractOut.licensee)?.name ??
    contractOut.licensee_name ??
    ""
  );
}

/** DRF-style `ordering`: a field name, prefixed with "-" for descending. */
function sortBy<T>(
  rows: T[],
  ordering: string | null,
  value: (row: T, field: string) => unknown,
): T[] {
  if (!ordering) return rows;
  const descending = ordering.startsWith("-");
  const field = descending ? ordering.slice(1) : ordering;
  return [...rows].sort((a, b) => {
    const left = value(a, field) ?? "";
    const right = value(b, field) ?? "";
    const comparison = String(left).localeCompare(String(right), undefined, { numeric: true });
    return descending ? -comparison : comparison;
  });
}

function matchesSearch(haystack: (string | null | undefined)[], query: string | null): boolean {
  if (!query) return true;
  const needle = query.trim().toLowerCase();
  return haystack.some((value) => (value ?? "").toLowerCase().includes(needle));
}

registerDevMockRoutes((request, url) => {
  const path = url.pathname.slice(basePath().length).replace(/\/$/, "") || "/";
  const method = request.method;
  const query = url.searchParams;
  const body = parseBody(request.body);
  const store = load();

  // --- Label -------------------------------------------------------------
  const label = { id: LABEL_ID, name: LABEL_NAME, currency: "USD", payment_threshold: "50.00" };
  if (path === "/accounts/labels" && method === "GET") return jsonResponse(paginated([label]));
  if (path.startsWith("/accounts/labels/") && method === "GET") return jsonResponse(label);

  // --- Catalogue ---------------------------------------------------------
  if (path === "/catalogue/recordings" && method === "GET") {
    const search = query.get("q");
    const results = RECORDINGS.filter((recording) =>
      matchesSearch([recording.title, recording.isrc, recording.artist], search),
    ).map((recording) => recordingPayload(recording.id));
    return jsonResponse(paginated(results));
  }
  if (path.startsWith("/catalogue/recordings/") && method === "GET") {
    const recording = recordingPayload(path.slice("/catalogue/recordings/".length));
    return recording ? jsonResponse(recording) : jsonResponse({ detail: "Not found" }, 404);
  }
  const releaseRecordingsMatch = path.match(/^\/catalogue\/releases\/([^/]+)\/recordings$/);
  if (releaseRecordingsMatch && method === "GET") {
    const results = RECORDINGS.filter((rec) => rec.release === releaseRecordingsMatch[1]).map(
      (rec) => recordingPayload(rec.id),
    );
    return jsonResponse(paginated(results));
  }
  if (path === "/catalogue/releases" && method === "GET") {
    return jsonResponse(paginated(RELEASES.map((release) => releaseSummary(release.id))));
  }

  // --- Payees ------------------------------------------------------------
  if (path === "/contracts/payees" && method === "GET") {
    const results = PAYEES.filter((payee) => matchesSearch([payee.name], query.get("q")));
    return jsonResponse(paginated(results));
  }
  if (path.startsWith("/contracts/payees/") && method === "GET") {
    const payee = PAYEES.find((p) => p.id === path.slice("/contracts/payees/".length));
    return payee ? jsonResponse(payee) : jsonResponse({ detail: "Not found" }, 404);
  }

  // --- Licensees ---------------------------------------------------------
  if (path === "/contracts/licensees") {
    if (method === "GET") {
      const results = sortBy(
        store.licensees.filter((licensee) =>
          matchesSearch([licensee.name, licensee.legal_name], query.get("q")),
        ),
        query.get("ordering"),
        (licensee, field) => (licensee as unknown as Record<string, unknown>)[field],
      );
      return jsonResponse(paginated(results));
    }
    if (method === "POST") {
      const licensee = {
        ...body,
        id: newId("licensee"),
        label: LABEL_ID,
        label_name: LABEL_NAME,
        created_at: nowIso(),
        updated_at: nowIso(),
        can_delete: true,
      } as Licensee;
      store.licensees = [licensee, ...store.licensees];
      save(store);
      return jsonResponse(licensee, 201);
    }
  }

  // Before the single-licensee routes: this is a collection hanging off one.
  const licensedTracksMatch = path.match(/^\/contracts\/licensees\/([^/]+)\/licensed-tracks$/);
  if (licensedTracksMatch && method === "GET") {
    const [, licenseeId] = licensedTracksMatch;
    const licensee = store.licensees.find((l) => l.id === licenseeId);
    // Matched by name, the same key the licensee filter on the list uses.
    const name = licensee?.name ?? "";

    const rows = store.contractsOut
      .filter((contractOut) => licenseeNameOf(contractOut, store) === name)
      .flatMap((contractOut) =>
        contractOut.track_ids.map((trackId) => {
          const recording = RECORDINGS.find((rec) => rec.id === trackId);
          return {
            // A track licensed under two contracts out is two rows.
            id: `${contractOut.id}-${trackId}`,
            track: {
              id: trackId,
              title: recording?.title ?? trackId,
              mix_name: recording?.mix_name ?? null,
            },
            artists: recordingPayload(trackId)?.contributors ?? [],
            contract_out: { id: contractOut.id, name: contractOut.name },
          };
        }),
      );

    return jsonResponse(rows);
  }

  if (path.startsWith("/contracts/licensees/")) {
    const id = path.slice("/contracts/licensees/".length);
    const index = store.licensees.findIndex((licensee) => licensee.id === id);
    if (index === -1) return jsonResponse({ detail: "Not found" }, 404);

    if (method === "GET") return jsonResponse(store.licensees[index]);
    if (method === "PATCH" || method === "PUT") {
      const { label, ...rest } = body;
      const updated = { ...store.licensees[index], ...rest, updated_at: nowIso() } as Licensee;
      store.licensees[index] = updated;
      // The licensee's name is copied onto its contracts out, so a rename has to
      // reach them too — the same denormalisation a backend would have to undo.
      for (const contractOut of store.contractsOut) {
        if (contractOut.licensee === id) contractOut.licensee_name = updated.name;
      }
      save(store);
      return jsonResponse(updated);
    }
    if (method === "DELETE") {
      store.licensees.splice(index, 1);
      save(store);
      return jsonResponse(null, 204);
    }
  }

  // --- Contracts out -----------------------------------------------------
  // Before /contracts/{id}: "out" is a collection here, not a contract id.
  if (path === "/contracts/out") {
    if (method === "GET") {
      const contractIn = query.get("contract_in");
      const statuses = query.get("status")?.split(",").filter(Boolean) ?? [];
      const licensingTypes = query.get("licensing_type")?.split(",").filter(Boolean) ?? [];
      const licensees = query.get("licensee")?.split(",").filter(Boolean) ?? [];

      let results = store.contractsOut
        .filter((contractOut) =>
          matchesSearch([contractOut.name, licenseeNameOf(contractOut, store)], query.get("q")),
        )
        .filter((contractOut) => (statuses.length ? statuses.includes(contractOut.status) : true))
        .filter((contractOut) =>
          licensingTypes.length ? licensingTypes.includes(contractOut.licensing_type) : true,
        )
        .filter((contractOut) =>
          licensees.length ? licensees.includes(licenseeNameOf(contractOut, store)) : true,
        );

      results = sortBy(results, query.get("ordering"), (contractOut, field) =>
        field === "licensee_name"
          ? licenseeNameOf(contractOut, store)
          : (contractOut as unknown as Record<string, unknown>)[field],
      );

      if (contractIn) {
        // Only the contracts out that license tracks of this contract in, each
        // carrying which of its tracks they took.
        const contract = store.contracts.find((c) => c.id === contractIn);
        const contractTrackIds = new Set(contract?.track_ids ?? []);
        results = results.filter((contractOut) =>
          contractOut.track_ids.some((id) => contractTrackIds.has(id)),
        );
        return jsonResponse(
          paginated(
            results.map((contractOut) => ({
              ...contractOutPayload(contractOut, store),
              tracks_from_contract: contractOut.track_ids
                .filter((id) => contractTrackIds.has(id))
                .map((id) => ({
                  id,
                  title: RECORDINGS.find((rec) => rec.id === id)?.title ?? id,
                })),
            })),
          ),
        );
      }

      return jsonResponse(
        paginated(results.map((contractOut) => contractOutPayload(contractOut, store))),
      );
    }

    if (method === "POST") {
      const contractOut: StoredContractOut = {
        id: newId("contract-out"),
        name: body.name ?? "",
        status: body.status ?? "draft",
        licensing_type: body.licensing_type ?? "compilation",
        start_date: body.start_date ?? null,
        end_date: body.end_date ?? null,
        release_date: body.release_date ?? null,
        formats_released: body.formats_released ?? [],
        territory: body.territory ?? "worldwide",
        currency: body.currency ?? "USD",
        licensee_royalty_percent: body.licensee_royalty_percent ?? "",
        statement_period: body.statement_period ?? "half_yearly",
        advance_per_track: body.advance_per_track ?? "",
        recoupable: body.recoupable ?? "fully",
        recoupable_amount: body.recoupable_amount ?? null,
        additional_notes: body.additional_notes ?? null,
        licensee: body.licensee ?? null,
        licensee_name:
          body.licensee_name ?? store.licensees.find((l) => l.id === body.licensee)?.name ?? null,
        track_ids: body.track_ids ?? [],
        file: null,
        file_name: null,
      };
      store.contractsOut = [contractOut, ...store.contractsOut];
      save(store);
      return jsonResponse(contractOutPayload(contractOut, store), 201);
    }
  }

  if (path.startsWith("/contracts/out/")) {
    const id = path.slice("/contracts/out/".length);
    const index = store.contractsOut.findIndex((contractOut) => contractOut.id === id);
    if (index === -1) return jsonResponse({ detail: "Not found" }, 404);

    if (method === "GET") {
      return jsonResponse(contractOutDetailPayload(store.contractsOut[index], store));
    }
    if (method === "PATCH" || method === "PUT") {
      const { track_ids, label, ...rest } = body;
      const updated: StoredContractOut = {
        ...store.contractsOut[index],
        ...rest,
        ...(track_ids ? { track_ids } : {}),
      };
      // The licensee's name is copied onto the contract out, so picking a
      // different one has to bring its name along.
      if (rest.licensee) {
        updated.licensee_name =
          store.licensees.find((l) => l.id === rest.licensee)?.name ?? updated.licensee_name;
      }
      store.contractsOut[index] = updated;
      save(store);
      return jsonResponse(contractOutDetailPayload(updated, store));
    }
    if (method === "DELETE") {
      store.contractsOut.splice(index, 1);
      save(store);
      return jsonResponse(null, 204);
    }
  }

  // --- Contracts in ------------------------------------------------------
  if (path === "/contracts") {
    if (method === "GET") {
      const status = query.get("status")?.split(",").filter(Boolean) ?? [];
      const types = query.get("contract_type")?.split(",").filter(Boolean) ?? [];
      const results = store.contracts
        .filter((contract) => matchesSearch([contract.name], query.get("q")))
        .filter((contract) => (status.length ? status.includes(contract.status) : true))
        .filter((contract) => (types.length ? types.includes(contract.contract_type) : true))
        .map((contract) => contractPayload(contract, store));
      return jsonResponse(paginated(results));
    }
    if (method === "POST") {
      const contract: StoredContract = {
        id: newId("contract"),
        name: body.name ?? "",
        contract_type: body.contract_type ?? "recording",
        status: body.status ?? "draft",
        start_date: body.start_date ?? "",
        end_date: body.end_date ?? null,
        territory: body.territory ?? "worldwide",
        currency: body.currency ?? "USD",
        statement_period: body.statement_period ?? "monthly",
        label_share_percent: String(body.label_share_percent ?? ""),
        label_share_overrides: body.label_share_overrides ?? {},
        payment_threshold: body.payment_threshold ?? "0.00",
        advance_amount: body.advance_amount ?? "0.00",
        payees: body.payees ?? [],
        track_ids: [],
        file: null,
      };
      store.contracts = [contract, ...store.contracts];
      save(store);
      return jsonResponse(contractPayload(contract, store), 201);
    }
  }

  if (path.startsWith("/contracts/")) {
    const id = path.slice("/contracts/".length);
    const index = store.contracts.findIndex((contract) => contract.id === id);
    if (index === -1) return jsonResponse({ detail: "Not found" }, 404);

    if (method === "GET") {
      return jsonResponse(contractDetailPayload(store.contracts[index], store));
    }
    if (method === "PATCH" || method === "PUT") {
      const { label, file, ...rest } = body;
      const updated: StoredContract = {
        ...store.contracts[index],
        ...rest,
        ...(rest.label_share_percent !== undefined
          ? { label_share_percent: String(rest.label_share_percent) }
          : {}),
      };
      store.contracts[index] = updated;
      save(store);
      return jsonResponse(contractDetailPayload(updated, store));
    }
    if (method === "DELETE") {
      store.contracts.splice(index, 1);
      save(store);
      return jsonResponse(null, 204);
    }
  }

  // --- Linking tracks to a contract in ------------------------------------
  if (path === "/catalogue/recordings/contracts/batch-link" && method === "POST") {
    const contract = store.contracts.find((c) => c.id === body.contract_id);
    const ids: string[] = body.sound_recording_ids ?? [];
    if (contract) {
      contract.track_ids = [...new Set([...contract.track_ids, ...ids])];
      save(store);
    }
    return jsonResponse({ linked: ids.length, removed: 0, updated: 0 });
  }

  // One track at a time, the way the contract's Link tracks drawer does it.
  const trackContractMatch = path.match(
    /^\/catalogue\/recordings\/([^/]+)\/contracts(?:\/([^/]+))?$/,
  );
  if (trackContractMatch) {
    const [, trackId, contractIdInPath] = trackContractMatch;
    if (method === "GET") {
      const linked = store.contracts.filter((c) => c.track_ids.includes(trackId));
      return jsonResponse(
        linked.map((contract) => ({
          id: `${trackId}-${contract.id}`,
          contract: contract.id,
          contract_details: contractPayload(contract, store),
        })),
      );
    }
    if (method === "POST") {
      const contract = store.contracts.find((c) => c.id === body.contract);
      if (!contract) return jsonResponse({ detail: "Not found" }, 404);
      contract.track_ids = [...new Set([...contract.track_ids, trackId])];
      save(store);
      return jsonResponse({
        id: `${trackId}-${contract.id}`,
        contract: contract.id,
        contract_details: contractPayload(contract, store),
      });
    }
    if (method === "DELETE" && contractIdInPath) {
      const contract = store.contracts.find((c) => c.id === contractIdInPath);
      if (contract) {
        contract.track_ids = contract.track_ids.filter((id) => id !== trackId);
        save(store);
      }
      return jsonResponse(null, 204);
    }
  }

  // --- Revenue statements ------------------------------------------------
  if (path === "/revenue/statements" && method === "GET") {
    const settled = query.get("is_settled");
    const vendors = query.get("vendor")?.split(",").filter(Boolean) ?? [];
    const results = store.revenueStatements.filter(
      (statement) =>
        (settled === null || String(statement.is_settled) === settled) &&
        (vendors.length === 0 || vendors.includes(statement.vendor)) &&
        matchesSearch(
          [VENDORS.find((v) => v.id === statement.vendor)?.name, statement.file_name],
          query.get("q"),
        ),
    );
    return jsonResponse(paginated(results));
  }
  if (path.startsWith("/revenue/statements/") && method === "GET") {
    const id = path.slice("/revenue/statements/".length);
    const statement = store.revenueStatements.find((s) => s.id === id);
    return statement ? jsonResponse(statement) : jsonResponse({ detail: "Not found" }, 404);
  }
  if (path.startsWith("/revenue/statements/") && method === "DELETE") {
    const id = path.slice("/revenue/statements/".length);
    store.revenueStatements = store.revenueStatements.filter((s) => s.id !== id);
    save(store);
    return jsonResponse(null, 204);
  }

  // --- Simple sales ------------------------------------------------------
  if (path === "/revenue/simple-sales") {
    if (method === "GET") {
      const results = store.simpleSales
        .map((entry) => simpleSalesPayload(entry, store))
        .filter((entry) =>
          matchesSearch(
            [
              entry.contract_out?.name,
              entry.contract_out?.licensee_name,
              entry.reference,
              ...entry.lines.map((line) => line.track.title),
              entry.release?.title,
            ],
            query.get("q"),
          ),
        )
        .sort((a, b) => b.sales_date.localeCompare(a.sales_date));
      return jsonResponse(paginated(results));
    }
    if (method === "POST") {
      const entry = storedSimpleSalesFrom(body);
      store.simpleSales.push(entry);
      save(store);
      return jsonResponse(simpleSalesPayload(entry, store), 201);
    }
  }
  if (path.startsWith("/revenue/simple-sales/")) {
    const id = path.slice("/revenue/simple-sales/".length);
    const index = store.simpleSales.findIndex((entry) => entry.id === id);
    if (index === -1) return jsonResponse({ detail: "Not found" }, 404);
    if (method === "GET") return jsonResponse(simpleSalesPayload(store.simpleSales[index], store));
    if (method === "PATCH" || method === "PUT") {
      store.simpleSales[index] = storedSimpleSalesFrom(body, store.simpleSales[index]);
      save(store);
      return jsonResponse(simpleSalesPayload(store.simpleSales[index], store));
    }
    if (method === "DELETE") {
      store.simpleSales.splice(index, 1);
      save(store);
      return jsonResponse(null, 204);
    }
  }

  // --- Revenue upload ----------------------------------------------------
  if (path === "/imports/vendors" && method === "GET") {
    return jsonResponse(
      SHOW_LICENSEE_STATEMENTS ? VENDORS : VENDORS.filter((v) => !v.requires_contract_out),
    );
  }
  if (path === "/imports/batches" && method === "POST") {
    // The prototype changes the upload form, not the import that follows, so the
    // batch is only acknowledged — the page returns to Revenue from here.
    return jsonResponse(
      {
        id: newId("batch"),
        label: LABEL_ID,
        name: null,
        vendor: String(body.vendor ?? ""),
        file_name: body.file instanceof File ? body.file.name : null,
        file_size: null,
        status: "pending",
        progress: 0,
        counters: null,
        error: null,
        created_at: nowIso(),
        updated_at: nowIso(),
      },
      201,
    );
  }

  return null;
});
